/**
 * The library: one row per report, with its generated files folded underneath.
 *
 * The page it replaces listed three different things in one table: saved
 * reports, every generated file, and every schedule. A report that ran forty
 * times was forty rows with the same name. Here a report is listed once and its
 * files are its runs.
 *
 * How a file finds its report (first match wins, and only on facts the server
 * recorded — nothing is guessed from similar-looking text beyond a name):
 *
 *   1. A file made by a schedule belongs to the report that schedule belongs to.
 *   2. A schedule belongs to the saved report whose id is its `pipelineViewId`;
 *      failing that, to the saved report of the same kind and the same name.
 *   3. A file made from a saved report (Run now records its id) belongs to it.
 *   4. A file from before that was recorded belongs to the saved report of the
 *      same kind whose name is the file's title.
 *   5. Everything else is grouped by template, deal and title, so forty
 *      "Pipeline Report" files are one row.
 *
 * Pure: the clock and the time zone come in as arguments.
 */
import type { Loan } from '@/types'
import type { ReportDefinition } from '@/services/reportDefinitions'
import type { ReportHistoryItem } from '@/services/reportingCenter'
import type { ReportSchedulerStatus } from '@/services/reportSchedules'
import type {
  ReportDeliveryHistoryItem,
  ReportSchedule,
} from '@/pages/pipeline-intelligence/types'
import { reportTypeSpec } from '../reportTypes'
import { FAILED_WINDOW_DAYS } from './attention'
import { isFavorite } from './favorites'
import {
  TYPE_BUCKETS,
  bucketOfDefinitionType,
  bucketOfTemplate,
  displayTemplate,
  engineTemplateOf,
  normalizeName,
  type TypeBucket,
  type TypeBucketKey,
} from './catalog'
import { compareInstants, endSentence, formatAgo, parseInstant } from './format'
import {
  HEALTH_SEVERITY,
  failureReason,
  scheduleHealth,
  type AttentionReason,
  type ScheduleHealth,
} from './scheduleHealth'

const NO_FAVORITES: ReadonlySet<string> = new Set()

/** How many days of activity the row's bar chart covers. */
export const ACTIVITY_DAYS = 14

export type RunTrigger = 'scheduled' | 'manual'

export type LibraryRun = {
  id: string
  at: string | null
  trigger: RunTrigger
  /** False for a file whose generation failed. */
  ok: boolean
  format: 'pdf' | 'xlsx'
  sizeBytes: number | null
  /** The archive still holds the file, so it can be downloaded. */
  downloadable: boolean
  title: string
  generatedBy: string | null
  scheduleId: string | null
  /** A plain sentence about the file: who or what made it, and anything unusual. */
  note: string
}

/** A failure the scheduler recorded: a run that failed outright, or a message the mail provider refused. */
export type LibraryFailure = {
  id: string
  at: string | null
  scheduleId: string
  kind: 'run' | 'delivery'
  recipient: string | null
  reason: string
}

export type ActivityDay = { day: string; runs: number; failures: number }

export type LibraryKind = 'saved' | 'schedule' | 'generated'

export type LibraryReport = {
  /** `def:<id>`, `sch:<id>` or `gen:<template>|<deal>|<title>`. Stable across reloads. */
  key: string
  kind: LibraryKind
  name: string
  template: string
  bucket: TypeBucket
  /** Portfolio, a deal's name, Committee, Period. */
  scope: string
  loanId: string | null
  /** Who owns it, as written on screen ("You", an address, "System"). */
  owner: string
  isMine: boolean
  isSystem: boolean
  isSharedWithMe: boolean
  /** "Private", "Shared with the workspace", "Shared with 2 people". */
  sharing: string
  version: number | null
  /** "PDF and Excel", "PDF". */
  formats: string
  definition: ReportDefinition | null
  schedules: ReportSchedule[]
  health: Array<{ schedule: ReportSchedule; health: ScheduleHealth }>
  /** Newest first. */
  runs: LibraryRun[]
  failures: LibraryFailure[]
  activity: ActivityDay[]
  /** The most recent thing that happened, run or failure. */
  last: { at: string; ok: boolean; reason: string | null } | null
  /** A short sentence for the row's banner, when something needs saying. */
  flag: { tone: 'bad' | 'warn'; text: string } | null
  attention: AttentionReason[]
  /** The next real run across this report's schedules, if any will run. */
  nextRun: { at: string; scheduleName: string; timeZone: string; text: string } | null
  /** Address count across the schedules (distinct, lower-cased). */
  recipientCount: number
  /** For sorting when nothing has run yet. */
  touchedAt: string | null
}

export type LibraryInputs = {
  definitions: ReportDefinition[]
  schedules: ReportSchedule[]
  history: ReportHistoryItem[]
  deliveries: ReportDeliveryHistoryItem[]
  deals?: Array<Pick<Loan, 'id' | 'property'>>
  me?: string | null
  status?: ReportSchedulerStatus
  now?: Date
  /** Day boundaries for the activity bars. Defaults to the browser's zone. */
  timeZone?: string
}

const PDF_AND_EXCEL = 'PDF and Excel'

function formatsLabel(formats: Array<'pdf' | 'xlsx' | 'digest'>): string {
  const unique = [...new Set(formats)]
  const hasPdf = unique.includes('pdf')
  const hasXlsx = unique.includes('xlsx')
  const hasDigest = unique.includes('digest')
  const parts = [hasPdf && hasXlsx ? PDF_AND_EXCEL : hasXlsx ? 'Excel' : 'PDF']
  if (hasDigest) parts.push('digest email')
  return parts.join(' and ')
}

function runFormat(value: string | null | undefined): 'pdf' | 'xlsx' {
  return String(value ?? '').toLowerCase() === 'xlsx' ? 'xlsx' : 'pdf'
}

function dayKey(date: Date, timeZone?: string): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date)
}

/** The last `ACTIVITY_DAYS` days, oldest first, with how many files and failures each held. */
export function activityByDay(
  runs: Array<Pick<LibraryRun, 'at' | 'ok'>>,
  failures: Array<Pick<LibraryFailure, 'at'>>,
  now: Date,
  timeZone?: string,
): ActivityDay[] {
  const days: ActivityDay[] = []
  const index = new Map<string, ActivityDay>()
  for (let back = ACTIVITY_DAYS - 1; back >= 0; back -= 1) {
    const day = dayKey(new Date(now.valueOf() - back * 86_400_000), timeZone)
    const entry = { day, runs: 0, failures: 0 }
    days.push(entry)
    index.set(day, entry)
  }
  for (const run of runs) {
    if (!run.at) continue
    const when = parseInstant(run.at)
    const entry = when ? index.get(dayKey(when, timeZone)) : undefined
    if (!entry) continue
    if (run.ok) entry.runs += 1
    else entry.failures += 1
  }
  for (const failure of failures) {
    if (!failure.at) continue
    const when = parseInstant(failure.at)
    const entry = when ? index.get(dayKey(when, timeZone)) : undefined
    if (entry) entry.failures += 1
  }
  return days
}

function runFromHistory(item: ReportHistoryItem): LibraryRun {
  const ok = item.status !== 'failed'
  const trigger: RunTrigger = item.trigger ?? (item.scheduleRunId ? 'scheduled' : 'manual')
  const kept = Boolean(item.downloadable)
  const note = !ok
    ? item.error || 'Generation failed'
    : !kept
      ? 'The file was not archived'
      : trigger === 'scheduled'
        ? 'Made by the schedule'
        : item.scheduledReportId
          ? 'Run now on the schedule, not emailed'
          : item.generatedBy
            ? `Run by ${item.generatedBy}`
            : 'Run by hand'
  return {
    id: item.id,
    at: item.createdAt,
    trigger,
    ok,
    format: runFormat(item.format),
    sizeBytes: Number.isFinite(item.sizeBytes) ? item.sizeBytes : null,
    downloadable: kept,
    title: item.title,
    generatedBy: item.generatedBy,
    scheduleId: item.scheduledReportId ?? null,
    note,
  }
}

function failureFromDelivery(item: ReportDeliveryHistoryItem): LibraryFailure | null {
  const status = String(item.status ?? '').toLowerCase()
  if (status !== 'failed' && status !== 'bounced') return null
  const runLevel = !item.recipient
  return {
    id: item.attemptId ?? item.runId,
    at: item.completedAt ?? item.sentAt ?? item.scheduledFor ?? null,
    scheduleId: item.scheduleId,
    kind: runLevel ? 'run' : 'delivery',
    recipient: item.recipient,
    reason: (item.error ?? '').trim() || (runLevel ? 'No reason was recorded' : 'The mail provider refused it'),
  }
}

type Draft = Omit<
  LibraryReport,
  'health' | 'runs' | 'failures' | 'activity' | 'last' | 'flag' | 'attention' | 'nextRun' | 'recipientCount' | 'touchedAt'
> & { runs: LibraryRun[]; failures: LibraryFailure[] }

function scopeLabel(definition: ReportDefinition, deals: Array<Pick<Loan, 'id' | 'property'>>): string {
  const spec = reportTypeSpec(definition.reportType)
  const loanId = typeof definition.filters.loan_id === 'string' ? definition.filters.loan_id : null
  if (loanId) return deals.find((deal) => deal.id === loanId)?.property || loanId
  if (spec.scope === 'committee') return 'Committee'
  if (spec.scope === 'period') return 'Period'
  if (spec.scope === 'deal') return 'One deal'
  return 'Portfolio'
}

function sharingLabel(definition: ReportDefinition, mine: boolean): string {
  if (definition.isShared) return mine ? 'Shared with the workspace' : 'Shared with you'
  const named = new Set([...definition.editors, ...definition.viewers].map((email) => email.toLowerCase()))
  named.delete(definition.ownerEmail.toLowerCase())
  if (named.size > 0) return `Shared with ${named.size} ${named.size === 1 ? 'person' : 'people'}`
  // Visible to someone who does not own it and was not named: an administrator.
  return mine ? 'Private' : 'Private to its owner'
}

export function buildLibrary(inputs: LibraryInputs): LibraryReport[] {
  const now = inputs.now ?? new Date()
  const deals = inputs.deals ?? []
  const me = (inputs.me ?? '').toLowerCase()
  const eventLabel = (type: string) =>
    inputs.status?.productEvents?.find((event) => event.type === type)?.label ?? type
  const drafts = new Map<string, Draft>()

  for (const definition of inputs.definitions) {
    const spec = reportTypeSpec(definition.reportType)
    const mine = Boolean(me) && definition.ownerEmail.toLowerCase() === me
    const loanId = typeof definition.filters.loan_id === 'string' ? definition.filters.loan_id : null
    drafts.set(`def:${definition.id}`, {
      key: `def:${definition.id}`,
      kind: 'saved',
      name: definition.name,
      template: spec.engineTemplate,
      bucket: bucketOfDefinitionType(definition.reportType),
      scope: scopeLabel(definition, deals),
      loanId,
      owner: definition.isSystem ? 'System' : mine ? 'You' : definition.ownerEmail,
      isMine: mine,
      isSystem: definition.isSystem,
      isSharedWithMe:
        !mine &&
        !definition.isSystem &&
        (definition.isShared ||
          [...definition.editors, ...definition.viewers].some((email) => email.toLowerCase() === me)),
      sharing: definition.isSystem ? 'Shared with everyone' : sharingLabel(definition, mine),
      version: definition.version,
      formats: formatsLabel(spec.formats),
      definition,
      schedules: [],
      runs: [],
      failures: [],
    })
  }

  const draftOfSchedule = new Map<string, string>()
  // The same name on the same template, or, for the pipeline family only, on any pipeline template:
  // a saved pipeline report prints on any of them (the summary, the Backshop layout, Current Status),
  // so "same template" would miss the files it actually made. A shelf is not evidence of the same
  // report elsewhere: a Deal Dossier, a Term Sheet Summary and a deal workbook share a shelf and
  // are three different documents.
  const PIPELINE_SHELF = 'pip'
  const definitionsByNameAndTemplate = (template: string, name: string) =>
    inputs.definitions.find(
      (definition) =>
        (engineTemplateOf(definition.reportType) === template ||
          (bucketOfDefinitionType(definition.reportType).key === PIPELINE_SHELF &&
            bucketOfTemplate(template).key === PIPELINE_SHELF)) &&
        normalizeName(definition.name) === normalizeName(name),
    )

  for (const schedule of inputs.schedules) {
    const viaView = schedule.pipelineViewId ? drafts.get(`def:${schedule.pipelineViewId}`) : undefined
    // A name match is only a match for the same deal: a definition scoped to one deal never
    // takes another deal's schedule, and its files with it. A portfolio-wide definition has no
    // deal, so it matches on name and family alone.
    const scheduleLoan = typeof schedule.filters.loan_id === 'string' ? schedule.filters.loan_id : null
    const byName = viaView ? undefined : definitionsByNameAndTemplate(schedule.template, schedule.name)
    const viaName =
      byName && (typeof byName.filters.loan_id !== 'string' || byName.filters.loan_id === scheduleLoan) ? byName : undefined
    const target = viaView ?? (viaName ? drafts.get(`def:${viaName.id}`) : undefined)
    if (target) {
      target.schedules.push(schedule)
      draftOfSchedule.set(schedule.id, target.key)
      continue
    }
    const key = `sch:${schedule.id}`
    const loanId = typeof schedule.filters.loan_id === 'string' ? schedule.filters.loan_id : null
    const creator = schedule.createdBy ?? null
    const mine = Boolean(me) && Boolean(creator) && creator!.toLowerCase() === me
    drafts.set(key, {
      key,
      kind: 'schedule',
      name: schedule.name || displayTemplate(schedule.template),
      template: schedule.template,
      bucket: bucketOfTemplate(schedule.template),
      scope: loanId ? deals.find((deal) => deal.id === loanId)?.property || loanId : 'Portfolio',
      loanId,
      owner: mine ? 'You' : creator || 'Scheduler',
      isMine: mine,
      isSystem: false,
      isSharedWithMe: false,
      sharing: 'Visible to the workspace',
      version: null,
      formats: formatsLabel([String(schedule.format).toLowerCase() as 'pdf' | 'xlsx' | 'digest']),
      definition: null,
      schedules: [schedule],
      runs: [],
      failures: [],
    })
    draftOfSchedule.set(schedule.id, key)
  }

  for (const item of inputs.history) {
    const run = runFromHistory(item)
    let key = item.scheduledReportId ? draftOfSchedule.get(item.scheduledReportId) : undefined
    // A file made from a saved report says so; that is a fact the server recorded, so it comes
    // before any matching on names.
    if (!key && item.definitionId && drafts.has(`def:${item.definitionId}`)) key = `def:${item.definitionId}`
    if (!key) {
      const definition = definitionsByNameAndTemplate(item.template, item.title)
      const sameDeal =
        !definition ||
        typeof definition.filters.loan_id !== 'string' ||
        definition.filters.loan_id === item.loanId
      if (definition && sameDeal) key = `def:${definition.id}`
    }
    if (!key) key = `gen:${item.template}|${item.loanId ?? ''}|${normalizeName(item.title)}`
    let draft = drafts.get(key)
    if (!draft) {
      const mine = Boolean(me) && Boolean(item.generatedBy) && item.generatedBy!.toLowerCase() === me
      draft = {
        key,
        kind: 'generated',
        name: item.title || displayTemplate(item.template),
        template: item.template,
        bucket: bucketOfTemplate(item.template),
        scope: item.loanId ? deals.find((deal) => deal.id === item.loanId)?.property || item.loanId : 'Portfolio',
        loanId: item.loanId,
        owner: mine ? 'You' : item.generatedBy || 'System',
        isMine: mine,
        isSystem: false,
        isSharedWithMe: false,
        sharing: 'Visible to the workspace',
        version: null,
        formats: formatsLabel([run.format]),
        definition: null,
        schedules: [],
        runs: [],
        failures: [],
      }
      drafts.set(key, draft)
    }
    draft.runs.push(run)
    if (draft.kind === 'generated') {
      // A generated group is "mine" if I made any of its files.
      if (Boolean(me) && item.generatedBy?.toLowerCase() === me) {
        draft.isMine = true
        draft.owner = 'You'
      }
      const formats = new Set(draft.runs.map((entry) => entry.format))
      draft.formats = formatsLabel([...formats])
    }
  }

  for (const delivery of inputs.deliveries) {
    const failure = failureFromDelivery(delivery)
    if (!failure) continue
    const key = draftOfSchedule.get(delivery.scheduleId)
    if (key) drafts.get(key)?.failures.push(failure)
  }

  const finished = [...drafts.values()].map((draft): LibraryReport => {
    const runs = [...draft.runs].sort((a, b) => compareInstants(b.at, a.at))
    const failures = [...draft.failures].sort((a, b) => compareInstants(b.at, a.at))
    const health = draft.schedules.map((schedule) => ({
      schedule,
      health: scheduleHealth(schedule, { now, status: inputs.status, eventLabel }),
    }))
    const attention = [
      ...new Set(health.map((entry) => entry.health.attention).filter((value): value is AttentionReason => Boolean(value))),
    ]
    // A failed generation the scheduler recorded in the last week is a reason to look,
    // even if the schedule has since run again.
    const recentFailure = failures.find(
      (failure) => {
        const when = parseInstant(failure.at)
        return Boolean(when) && now.valueOf() - (when as Date).valueOf() <= FAILED_WINDOW_DAYS * 86_400_000
      },
    )
    if (recentFailure && !attention.includes('failed')) attention.push('failed')
    if (runs[0] && !runs[0].ok && !attention.includes('failed')) attention.push('failed')

    const candidates: Array<{ at: string; ok: boolean; reason: string | null }> = []
    if (runs[0]?.at) candidates.push({ at: runs[0].at, ok: runs[0].ok, reason: runs[0].ok ? null : runs[0].note })
    if (failures[0]?.at) candidates.push({ at: failures[0].at, ok: false, reason: failures[0].reason })
    for (const entry of health) {
      if (entry.schedule.lastRunAt) {
        candidates.push({
          at: entry.schedule.lastRunAt,
          ok: failureReason(entry.schedule.lastRunStatus) === null && !/^failed/i.test(entry.schedule.lastRunStatus ?? ''),
          reason: failureReason(entry.schedule.lastRunStatus),
        })
      }
    }
    candidates.sort((a, b) => compareInstants(b.at, a.at))
    const last = candidates[0] ?? null

    const upcoming = health
      .filter((entry) => entry.health.nextRunAt)
      .sort((a, b) => compareInstants(a.health.nextRunAt, b.health.nextRunAt))[0]
    const nextRun = upcoming
      ? {
          at: upcoming.health.nextRunAt as string,
          scheduleName: upcoming.schedule.name,
          timeZone: upcoming.schedule.timezone,
          text: upcoming.health.nextRunText,
        }
      : null

    const recipients = new Set(
      draft.schedules
        .filter((schedule) => schedule.deliveryMode !== 'none')
        .flatMap((schedule) => schedule.recipients.map((address) => address.toLowerCase())),
    )

    let flag: LibraryReport['flag'] = null
    const worst = [...health].sort(
      (a, b) => HEALTH_SEVERITY[a.health.state] - HEALTH_SEVERITY[b.health.state],
    )[0]
    if (last && !last.ok) {
      flag = {
        tone: 'bad',
        text: endSentence(`The last run failed${last.reason ? `: ${last.reason}` : ''}`),
      }
    } else if (recentFailure) {
      flag = {
        tone: 'warn',
        text: `${endSentence(`A run failed ${formatAgo(recentFailure.at, now)}: ${recentFailure.reason}`)} The latest run went through.`,
      }
    } else if (worst && (worst.health.attention === 'overdue' || worst.health.attention === 'no_next_run')) {
      flag = { tone: 'bad', text: worst.health.detail }
    } else if (worst && worst.health.attention === 'draft') {
      flag = { tone: 'warn', text: worst.health.detail }
    } else {
      const waiting = health.find(
        (entry) =>
          entry.schedule.isActive &&
          entry.schedule.deliveryMode !== 'none' &&
          entry.schedule.recipients.length > 0 &&
          entry.schedule.willEmail === false &&
          (entry.schedule.deliveryBlockers?.length ?? 0) > 0,
      )
      if (waiting) {
        const count = waiting.schedule.recipients.length
        flag = {
          tone: 'warn',
          text: `Not emailed. ${waiting.schedule.deliveryBlockers![0].label} The ${count} ${count === 1 ? 'recipient is' : 'recipients are'} waiting; files are generated and kept.`,
        }
      }
    }

    return {
      ...draft,
      runs,
      failures,
      health,
      activity: activityByDay(runs, failures, now, inputs.timeZone),
      last,
      flag,
      attention,
      nextRun,
      recipientCount: recipients.size,
      touchedAt: last?.at ?? draft.definition?.updatedAt ?? draft.definition?.createdAt ?? null,
    }
  })

  return finished
}

// ---------------------------------------------------------------------------
// Collections, search and order
// ---------------------------------------------------------------------------

export type CollectionId =
  | 'all'
  | 'favorites'
  | 'mine'
  | 'shared'
  | 'scheduled'
  | 'attention'
  | 'system'
  | `type:${TypeBucketKey}`

export const LIBRARY_COLLECTIONS: Array<{ id: CollectionId; label: string }> = [
  { id: 'all', label: 'All reports' },
  { id: 'favorites', label: 'Favorites' },
  { id: 'mine', label: 'Mine' },
  { id: 'shared', label: 'Shared with me' },
  { id: 'scheduled', label: 'Scheduled' },
  { id: 'attention', label: 'Needs attention' },
  { id: 'system', label: 'System views' },
]

export function inCollection(
  report: LibraryReport,
  collection: CollectionId,
  favorites: ReadonlySet<string> = NO_FAVORITES,
): boolean {
  switch (collection) {
    case 'all':
      return true
    case 'favorites':
      return isFavorite(report, favorites)
    case 'mine':
      return report.isMine
    case 'shared':
      return report.isSharedWithMe
    case 'scheduled':
      return report.schedules.length > 0
    case 'attention':
      return report.attention.length > 0
    case 'system':
      return report.isSystem
    default:
      return report.bucket.key === collection.slice('type:'.length)
  }
}

export function collectionCounts(
  reports: LibraryReport[],
  favorites: ReadonlySet<string> = NO_FAVORITES,
): Record<string, number> {
  const counts: Record<string, number> = {}
  for (const { id } of LIBRARY_COLLECTIONS) {
    counts[id] = reports.filter((report) => inCollection(report, id, favorites)).length
  }
  for (const bucket of TYPE_BUCKETS) {
    counts[`type:${bucket.key}`] = reports.filter((report) => report.bucket.key === bucket.key).length
  }
  return counts
}

function haystack(report: LibraryReport): string {
  return [
    report.name,
    report.bucket.name,
    report.bucket.code,
    displayTemplate(report.template),
    report.owner,
    report.definition?.ownerEmail ?? '',
    report.scope,
    report.loanId ?? '',
    ...report.schedules.flatMap((schedule) => [schedule.name, ...schedule.recipients]),
  ]
    .join(' ')
    .toLowerCase()
}

export type LibrarySort = 'recent' | 'name'

export function filterLibrary(
  reports: LibraryReport[],
  options: { collection: CollectionId; query: string; sort: LibrarySort; favorites?: ReadonlySet<string> },
): LibraryReport[] {
  const favorites = options.favorites ?? NO_FAVORITES
  const tokens = options.query.toLowerCase().split(/\s+/).filter(Boolean)
  const visible = reports.filter(
    (report) =>
      inCollection(report, options.collection, favorites) &&
      tokens.every((token) => haystack(report).includes(token)),
  )
  const sorted = [...visible]
  if (options.sort === 'name') {
    sorted.sort((a, b) => a.name.localeCompare(b.name, 'en-US', { sensitivity: 'base' }))
  } else {
    sorted.sort(
      (a, b) =>
        compareInstants(b.touchedAt, a.touchedAt) ||
        a.name.localeCompare(b.name, 'en-US', { sensitivity: 'base' }),
    )
  }
  // A starred report is where you look first, in whatever order the rest are in. The sort above is
  // stable, so the order inside each group is the one that was asked for.
  return sorted
    .map((report, index) => ({ report, index }))
    .sort(
      (a, b) =>
        Number(isFavorite(b.report, favorites)) - Number(isFavorite(a.report, favorites)) || a.index - b.index,
    )
    .map((entry) => entry.report)
}

export function libraryTotals(reports: LibraryReport[]): { reports: number; files: number } {
  return { reports: reports.length, files: reports.reduce((sum, report) => sum + report.runs.length, 0) }
}

/** "Last run 3d ago" etc. for a row that has never run. */
export function lastRunText(report: LibraryReport, now: Date): string {
  if (!report.last) return report.schedules.length ? 'Not run yet' : 'Never run'
  return formatAgo(report.last.at, now)
}


/** The newest stored, successful file in a format (or in any format). */
export function latestFile(report: Pick<LibraryReport, 'runs'>, format?: 'pdf' | 'xlsx'): LibraryRun | null {
  return report.runs.find((run) => run.ok && run.downloadable && (!format || run.format === format)) ?? null
}

export type TimelineEntry =
  | { kind: 'run'; at: string; run: LibraryRun }
  | { kind: 'failure'; at: string; failure: LibraryFailure }

/** Runs and the failures the scheduler recorded, as one timeline, newest first. */
export function timelineOf(report: Pick<LibraryReport, 'runs' | 'failures'>): TimelineEntry[] {
  const entries: TimelineEntry[] = [
    ...report.runs.map((run): TimelineEntry => ({ kind: 'run', at: run.at ?? '', run })),
    ...report.failures
      .filter((failure) => failure.kind === 'run')
      .map((failure): TimelineEntry => ({ kind: 'failure', at: failure.at ?? '', failure })),
  ]
  return entries.sort((a, b) => compareInstants(b.at, a.at))
}
